/**
 * Oturum sonlandırmanın TEK yolu.
 *
 * Talep: "Web'de kullanıcı değiştiriyorum, veriler birbirine karışıyor — dashboard'da gördüm."
 * Sebep: çıkış dört ayrı yerde kopyalanmıştı ve hepsi yalnız `token` + `user` anahtarlarını silip
 * SPA içinde `navigate('/')` yapıyordu. Yönlendirme sayfayı yeniden yüklemediği için:
 *   • sessionStorage'daki dashboard cache'i (5 dk TTL) yaşıyor, sonraki kullanıcı önceki
 *     kullanıcının rakamlarını tek bir ağ isteği bile atılmadan görüyordu,
 *   • modül seviyesinde tutulan cache'ler (useDistributorUser, auditUsers) hiç sıfırlanmıyordu.
 *
 * Bu yüzden çıkış artık iki şeyi birlikte yapar: bilinen-iyi anahtarlar dışında TÜM storage'ı
 * boşaltır ve tam yeniden yükleme ile gider. Reload, hangi modül nerede cache tutuyor olursa olsun
 * JS heap'ini sıfırlar — yani yeni bir cache eklendiğinde burayı güncellemeyi unutmak sızıntı
 * doğurmaz. Anahtar silmeyi tek tek saymak (deny-list) o garantiyi vermez; allow-list + reload verir.
 */

import { getToken } from './auth';
import { recordSessionEvent, SESSION_LOG_KEY, SESSION_END_REASONS } from './sessionLog';

/**
 * Çıkıştan SAĞ ÇIKAN localStorage anahtarları. Buraya yalnız kullanıcıdan bağımsız
 * (ya da bilinçli olarak kullanıcı-ötesi) şeyler girer — iş verisi ASLA.
 */
const PRESERVED_LOCAL_KEYS = [
  'loginUser',              // "Beni Hatırla" — kullanıcının kendi tercihi
  'loginRemember',
  'datascope-theme-mode',   // tema/dil: cihaz tercihi
  'datascope-language',
  'ds_device_id',           // cihaz kimliği (syncManager); kullanıcıya değil makineye ait
  SESSION_LOG_KEY,          // çıkış teşhis günlüğü — silinirse "neden atıldım" cevapsız kalır
];

// db.delete() başka sekmeler bağlıyken 'blocked' olup asılabilir — çıkışı geciktirmesin.
const DB_DELETE_TIMEOUT_MS = 1500;

/**
 * Çıkışın diğer sekmelere duyurulduğu anahtar. localStorage paylaşımlı olduğu için
 * bir sekmedeki çıkış TÜM sekmelerin token'ını siler; diğer sekmeler bunu ancak
 * bir sonraki 401'de fark eder ve sebebini asla öğrenemezdi (sebep sessionStorage'da,
 * o da sekmeye özel). Bu sinyal ile diğer sekmeler olayı sebebiyle birlikte izler.
 */
const BROADCAST_KEY = 'ds_session_end_signal';
// Sinyal bu süreden eskiyse yoksayılır (login sonrası kalıntı tetiklemesin).
const BROADCAST_TTL_MS = 30 * 1000;

/**
 * Sekmeler arasında PAYLAŞILAN "son etkileşim" zaman damgası (#850).
 *
 * Talep: "Farklı sekmedeki hareketsizlik aktif sekmeyi kapatıyor, kapatmamalı."
 * SessionGuard hareketsizliği sekme içinde (useRef) sayıyordu. İki sekme açıkken
 * kullanıcı yalnız birinde çalışsa bile boştaki sekmenin sayacı doluyor,
 * endSession('idle') PAYLAŞILAN localStorage'ı temizliyor ve kullanıcının
 * ÇALIŞTIĞI sekme de düşüyordu.
 *
 * Doğru model: "en son ne zaman etkileşim oldu" sekmenin değil OTURUMUN
 * özelliğidir — o yüzden localStorage'da paylaşılır. Herhangi bir sekme aktifken
 * hiçbir sekme idle saymaz; tüm sekmeler gerçekten boştaysa hepsi kapanır.
 *
 * Bu anahtar PRESERVED_LOCAL_KEYS'te DEĞİLDİR: çıkışta silinmeli, yoksa bir
 * sonraki kullanıcının oturumu önceki kullanıcının aktivitesini devralır.
 */
const ACTIVITY_KEY = 'ds_last_activity';
// Her mousemove'da localStorage'a yazmak pahalıdır; bu aralıktan sık yazılmaz.
const ACTIVITY_WRITE_THROTTLE_MS = 5 * 1000;

let lastActivityWrite = 0;

/**
 * Bu sekmedeki etkileşimi diğer sekmelere duyurur. Throttle'lıdır — çağıran
 * taraf her fare hareketinde çağırabilir.
 */
export function markActivity(now = Date.now()) {
  if (now - lastActivityWrite < ACTIVITY_WRITE_THROTTLE_MS) return;
  lastActivityWrite = now;
  try { localStorage.setItem(ACTIVITY_KEY, String(now)); } catch { /* kota — idle sayımı yerelde sürer */ }
}

/**
 * Herhangi bir sekmede kaydedilmiş son etkileşim zamanı (ms epoch); yoksa 0.
 *
 * Hareketsizlik kapısı bir GÜVENLİK kontrolüdür (#259): terk edilmiş oturumu
 * kapatır. Bu yüzden GELECEĞE dönük bir değer (saat kayması, bozuk/elle yazılmış
 * kayıt) yok sayılır — aksi halde tek bir bozuk kayıt idle çıkışını sonsuza dek
 * devre dışı bırakırdı.
 */
export function getSharedLastActivity() {
  try {
    const raw = Number(localStorage.getItem(ACTIVITY_KEY));
    if (!Number.isFinite(raw) || raw <= 0) return 0;
    if (raw > Date.now() + ACTIVITY_WRITE_THROTTLE_MS) return 0;
    return raw;
  } catch {
    return 0;
  }
}

let ending = false;

function baseHref() {
  return document.getElementsByTagName('base')[0]?.getAttribute('href') || '/';
}

/** storage'ı boşaltır, `preserved` anahtarlarını geri yazar. */
function purge(storage, preserved) {
  try {
    const keep = preserved
      .map((key) => [key, storage.getItem(key)])
      .filter(([, value]) => value !== null);
    storage.clear();
    keep.forEach(([key, value]) => storage.setItem(key, value));
  } catch { /* private mode / kota — çıkışı engellemesin */ }
}

/**
 * Oturumu kapatır: storage'ı temizler, yerel IndexedDB'yi düşürür, login'e tam reload ile döner.
 *
 * @param {string} reason Sonlanma sebebi — bkz. sessionLog SESSION_END_REASONS.
 *   Hem teşhis günlüğüne hem de Login ekranının gösterdiği bilgilendirmeye gider.
 *   Elle çıkışta 'manual'dır; Login o sebep için mesaj göstermez (kullanıcı zaten bilir).
 * @param {object} [detail] Sebebe özel ölçümler (idle süresi, 401 dönen URL…) — yalnız günlüğe.
 */
export function endSession(reason = SESSION_END_REASONS.MANUAL, detail) {
  if (ending) return; // eşzamanlı 401'ler bir kez tetiklesin
  ending = true;

  // Temizlikten ÖNCE: kayıt kullanıcı adı ve token exp'i gibi oturum bilgilerini içerir.
  recordSessionEvent(reason, detail);

  purge(localStorage, PRESERVED_LOCAL_KEYS);
  purge(sessionStorage, []);
  // Temizlikten SONRA yazılır; sessionStorage reload'dan sağ çıkar, Login onu okuyup siler.
  if (reason) {
    try { sessionStorage.setItem('sessionEndReason', reason); } catch { /* yoksay */ }
  }

  // Diğer sekmeler de bu oturumu kaybetti (localStorage paylaşımlı) — sebebiyle duyur ki
  // sessizce 401'e düşmek yerine aynı mesajı gösterebilsinler.
  if (reason !== SESSION_END_REASONS.CROSS_TAB) {
    try {
      localStorage.setItem(BROADCAST_KEY, JSON.stringify({ reason, at: Date.now() }));
    } catch { /* yoksay */ }
  }

  window.dispatchEvent(new Event('auth:logout'));

  // Dexie'deki delta/kuyruk verisi de tenant'a özeldir — best-effort düşür, sonucu bekletme.
  // Dinamik import: bu modülü authInterceptor açılışta yükler, Dexie'yi ilk chunk'a çekmesin.
  const redirect = () => window.location.replace(baseHref());
  Promise.race([
    import('./syncDb').then((m) => m.default.delete()).catch(() => {}),
    new Promise((resolve) => setTimeout(resolve, DB_DELETE_TIMEOUT_MS)),
  ]).then(redirect, redirect);
}

/**
 * Başka bir sekmedeki çıkışı izler.
 *
 * Bir sekmedeki çıkış TÜM sekmeleri düşürür: localStorage paylaşımlıdır, token'ı
 * kim silerse herkes kaybeder. Bu izleyici olayı anında, kaynak sebebiyle birlikte
 * kaydeder — yoksa diğer sekmeler durumu ancak bir sonraki isteğin 401'inde görüp
 * sebepsiz biçimde login'e düşüyordu.
 *
 * NOT: "boştaki sekme çalıştığım sekmeyi düşürüyor" (#850) şikâyeti buradan
 * ÇÖZÜLMEZ — burası olayı yalnız raporlar. Asıl çözüm hareketsizliğin sekme
 * içinde değil oturum genelinde sayılmasıdır; bkz. markActivity /
 * getSharedLastActivity ve SessionGuard'ın idle kapısı.
 *
 * index.js'te bir kez kurulur.
 */
export function installCrossTabSessionWatcher() {
  if (window.__DATASCOPE_CROSS_TAB_WATCHER__) return;
  window.__DATASCOPE_CROSS_TAB_WATCHER__ = true;

  // Hiç giriş yapılmamış bir sekme (açık duran login ekranı) tetiklenmesin.
  let wasAuthenticated = !!getToken();
  window.addEventListener('auth:login', () => { wasAuthenticated = true; });

  window.addEventListener('storage', (e) => {
    if (e.key !== BROADCAST_KEY || !e.newValue) return;
    if (!wasAuthenticated || ending) return;

    let signal = {};
    try { signal = JSON.parse(e.newValue) || {}; } catch { /* yoksay */ }
    if (!signal.at || Date.now() - signal.at > BROADCAST_TTL_MS) return; // bayat kalıntı

    // Kaynak sebep de kayda girer: "başka sekme hareketsizlikten kapandı" ile
    // "başka sekmede elle çıkış yapıldı" sahada ancak böyle ayrılabiliyor.
    endSession(SESSION_END_REASONS.CROSS_TAB, {
      sourceReason: signal.reason ?? null,
      sourceAgoMs: Date.now() - signal.at,
    });
  });
}

export default endSession;
